import {DiffFileTree, File, type FileProps} from '@github-ui/diff-file-tree/file-tree'

import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {clsx} from 'clsx'

import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import {useThreadCommentCount} from '../../stores/selectors'
import type {DiffDelta, FileNode} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {memo, useCallback} from 'react'

export type PullRequestFileTreeProps = {
  className?: string
  filteredDiffs: PullRequestFileTreeDiff[] // filtered diff summaries
  onFileSelected?: (file?: DiffDelta) => void
  fileFilter: React.ReactElement
}

export const PR_FILE_TREE_ID = 'pr-file-tree'

/*
 * This component is intended to be visually and functionally consistent with the
 * @github-ui/commits FileTree component. Look for opportunities to upstream duplicated
 * code into the shared @github-ui/diff-file-tree package.
 */
export function PullRequestFileTree({
  className,
  fileFilter,
  filteredDiffs, // filtered diff summaries
  onFileSelected,
}: PullRequestFileTreeProps) {
  const renderFile = useCallback((props: FileProps) => <PullRequestFile key={props.file.filePath} {...props} />, [])

  return (
    <ErrorBoundary fallback={<span>File tree failed to load.</span>}>
      <div className={clsx('d-flex flex-column', className)} id={PR_FILE_TREE_ID}>
        <div className="pb-3 pr-3">{fileFilter}</div>

        <div style={{overflowY: 'auto'}}>
          <h2 className="sr-only">File tree</h2>
          <DiffFileTree
            diffs={filteredDiffs}
            fileNodeRenderer={renderFile}
            sortByDiffOrder
            onSelect={onFileSelected}
            className="pr-3"
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}

const PullRequestFile = memo(function PullRequestFile({file, ...props}: FileProps) {
  const threadIds = Object.values((file.diff as PullRequestFileTreeDiff)?.markersMap ?? {}).flatMap(marker =>
    marker.threads.map(thread => thread.id),
  )

  const diffCommentCount = useThreadCommentCount(threadIds)

  const fileWithComments: FileNode<DiffDelta> = {
    ...file,
    diff: {
      ...file.diff,
      totalCommentsCount: diffCommentCount,
    },
  }

  return <File file={fileWithComments} {...props} />
})

try{ PullRequestFileTree.displayName ||= 'PullRequestFileTree' } catch {}
try{ PullRequestFile.displayName ||= 'PullRequestFile' } catch {}